import type { FetchStatus } from "@/base/types"
import type { RootState } from "@/redux/root.reducer"

/* eslint-disable @typescript-eslint/consistent-type-imports */
import { getAuthStatusFromState } from "@/redux/auth.reducer"
import { getFetchStatusByName } from "@/redux/requests.reducer"
import {
  getIsAccountDeactivatedFromState,
  getIsDemoModeActiveFromState,
  getIsTermsOfServiceAcceptedFromState,
  getUserIsAdminFromState,
  getUserNameFamilyFromState,
  getUserNameGivenFromState,
} from "@/redux/selectors"
import {
  getActiveFarmSubscriptionDueDate,
  getFarmAccountSubscriptionLockDate,
  getRealFarmCountFromState,
} from "@/redux/user-farms.selectors"
import { createAsyncThunk } from "@reduxjs/toolkit"

import * as Models from "../redux/models"
import * as AppStorage from "./async-storage"
import { makeFileLogger } from "./logger"
import {
  GetActionArgumentsType,
  GetResponseDataType,
  sendRequest,
} from "./send-request"

const fileLogger = makeFileLogger({
  fileName: "load-app.ts",
})

function isPending(
  fetchStatus: FetchStatus | undefined,
): fetchStatus is "pending" | undefined {
  if (fetchStatus === "pending" || typeof fetchStatus === "undefined") {
    return true
  }
  return false
}

type AppViewName =
  | "acceptTermsOfService"
  | "accountDeactivated"
  | "addInitialPhoneNumber"
  | "auth"
  | "dashboard"
  | "farmAccountLockedDueToNonPayment"
  | "loadError"
  | "loading"
  | "noFarms"
  | "userNameForm"
  | "verifyInitialPhoneNumber"

export function getAppViewFromState(state: RootState): AppViewName {
  const allPhoneNumbers = Models.phoneNumber.selectAll(state)
  const authStatus = getAuthStatusFromState(state)
  const farmSubscriptionDueDate = getActiveFarmSubscriptionDueDate(state)
  const isAdminUser = getUserIsAdminFromState(state)
  const isDemo = getIsDemoModeActiveFromState(state)
  const isInitialFarmLoaded = state.userFarms.isFarmLoaded
  const isTosAccepted = getIsTermsOfServiceAcceptedFromState(state)
  const loadFarm = getFetchStatusByName(state, "LoadApp")
  const loadDeviceActivity = getFetchStatusByName(state, "LoadDeviceActivity")
  const loadUser = getFetchStatusByName(state, "LoadActiveUser")
  const nameFamily = getUserNameFamilyFromState(state)
  const nameGiven = getUserNameGivenFromState(state)
  const numFarms = getRealFarmCountFromState(state)
  const phoneNumberCount = allPhoneNumbers.length
  const prepareDemo = getFetchStatusByName(state, "PrepareDemoEnvironment")
  const exitDemo = getFetchStatusByName(state, "ExitDemoMode")
  const readLocalStorage = getFetchStatusByName(state, "ReadLocalStorage")
  const verifiedPhoneNumbers = allPhoneNumbers.filter(
    (phoneNumber) => phoneNumber.isVerified,
  )

  // Checking user authentication
  if (isPending(authStatus)) {
    return "loading"
  }

  // User is not authenticated
  if (authStatus === "rejected") {
    return "auth"
  }

  // Waiting for user data & local storage
  if (isPending(readLocalStorage) || isPending(loadUser)) {
    return "loading"
  }

  // DB failed to return user data
  if (loadUser === "rejected") {
    if (getIsAccountDeactivatedFromState(state)) {
      return "accountDeactivated"
    }
    return "loadError"
  }

  // load user is fulfilled...

  // User must accept terms of service
  if (!isTosAccepted) {
    return "acceptTermsOfService"
  }

  // Every user must input their name
  if (!Boolean(nameGiven) || !Boolean(nameFamily)) {
    return "userNameForm"
  }

  // Users must pay their subscription fee
  if (typeof farmSubscriptionDueDate !== "undefined") {
    const farmSubscriptionDueDateTimestamp = new Date(farmSubscriptionDueDate)

    const lockDate = getFarmAccountSubscriptionLockDate(
      farmSubscriptionDueDateTimestamp,
    )

    const now = new Date().getTime()
    if (lockDate.getTime() < now && !isAdminUser) {
      return "farmAccountLockedDueToNonPayment"
    }
  }
  // return "add-initial-phone-number"
  // Users must input their phone number
  if (state.userPhoneNumbers.promptStatus !== "skipped") {
    if (phoneNumberCount === 0) {
      return "addInitialPhoneNumber"
    }

    if (verifiedPhoneNumbers.length === 0) {
      return "verifyInitialPhoneNumber"
    }
  }

  // Starting the "dashboard"
  const isLoadingInitialFarm = loadFarm === "pending" && !isInitialFarmLoaded
  const isLoadingDeviceActivity = loadDeviceActivity === "pending" && !isInitialFarmLoaded

  // Show loading if we are loading the demo app
  if (prepareDemo === "pending" || exitDemo === "pending") {
    return "loading"
  }
  // Show loading for initial farm (each time farm id changes)
  if (isLoadingInitialFarm || isLoadingDeviceActivity) {
    return "loading"
  }
  // Database failed to load the farm app (only for initial load, not refreshes)
  if (!isInitialFarmLoaded && (loadFarm === "rejected" || loadDeviceActivity === "rejected")) {
    return "loadError"
  }
  // Dashboard app or demo?
  if (isDemo) {
    return "dashboard"
  }
  // User needs to connect their user account with a farm acccount
  if (numFarms === 0) {
    return "noFarms"
  }
  return "dashboard"
}

export type AppView = ReturnType<typeof getAppViewFromState>

/**
 * Transform database values to app values here
 */
function handleActiveFarmResponse(response: Models.LoadActiveFarmResponse) {
  try {
    const fields = response.fields ?? []
    fileLogger.debug(`Processing ${fields.length} fields`)

    return {
      ...response,
      fields: fields.map((field) => Models.prepareField(field)),
    }
  } catch (error) {
    fileLogger.error('Error in handleActiveFarmResponse:', error)
    fileLogger.error('Response object:', JSON.stringify(response, null, 2))
    throw error
  }
}

/**
 *
 */
export const loadActiveFarmAsync = createAsyncThunk<
  GetResponseDataType<"LoadApp">,
  GetActionArgumentsType<"LoadApp">
>("LoadApp", async ({ activeFarmId }) => {
  try {
    const values = await sendRequest("LoadApp", {
      actionArguments: { activeFarmId },
      activeFarmId,
    })

    fileLogger.debug(`Received LoadApp response for farm ${activeFarmId}`)
    fileLogger.debug(`Response has fields: ${values.fields ? 'yes' : 'no'}`)

    // fill default values
    const responseData = handleActiveFarmResponse(values)
    if (responseData.errorCode === "NO FARM ASSOCIATION") {
      fileLogger.error("Clearing stored farm ids...")
      await AppStorage.clearStoredFarmIds()
    }

    return responseData
  } catch (error) {
    fileLogger.error(`Error in loadActiveFarmAsync for farm ${activeFarmId}:`, error)
    throw error
  }
})
